/**
 * Proveedores: a quién le compro y cuánto le debo.
 *
 * LAS DOS PREGUNTAS SON UNA SOLA, Y POR ESO VIVEN EN LA MISMA RUTA
 * ---------------------------------------------------------------
 * Sin `?id=` está el padrón, con el saldo de cada uno y el total abajo. Con
 * `?id=` la lista se reemplaza por la cuenta de ese proveedor, igual que hace
 * `/clientes`. Es master→detail por URL, no un modal: la ficha tiene extracto,
 * imputaciones y resumen mensual, y nada de eso entra en un diálogo sin romper
 * el botón de atrás ni la posibilidad de compartir el link.
 *
 * ESE `?id=` ES EL QUE USA EL BUSCADOR GLOBAL, y hasta 0062 abría el formulario
 * de edición: se buscaba un proveedor y se caía en su teléfono en vez de en su
 * cuenta. Ahora lleva a la cuenta, y editar es un botón adentro.
 *
 * La baja es lógica. Un proveedor no se borra nunca: sus compras y los productos
 * que lo referencian tienen que seguir apuntando a algo.
 */
import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Plus, Truck } from "lucide-react";

import { PageHeader } from "@/components/layout/PageHeader";
import { Pagina } from "@/components/layout/Pagina";
import { DialogProveedor } from "@/components/proveedores/DialogProveedor";
import { FichaProveedor } from "@/components/proveedores/FichaProveedor";
import { useFichaProveedor } from "@/components/proveedores/consultas";
import { ListaProveedores } from "@/components/proveedores/ListaProveedores";
import { deudaTecnico } from "@/components/taller/logica";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useActualizarProveedor, useProveedores, useReparaciones } from "@/lib/queries";
import { useCrearProveedor } from "@/components/compras/consultas";
import { mostrarError, mostrarExito } from "@/lib/errores";
import type { CambiosProveedor, Proveedor, SaldoProveedor } from "@/lib/tipos";

export const Route = createFileRoute("/proveedores")({
  component: Proveedores,
  // Con `id` se ve la cuenta de ese proveedor; sin él, la lista. Se valida en
  // vez de confiar: la URL la puede tipear cualquiera.
  validateSearch: (busqueda: Record<string, unknown>): { id?: number } => {
    const id = Number(busqueda.id);
    return Number.isInteger(id) && id > 0 ? { id } : {};
  },
});

function Proveedores(): JSX.Element {
  const { id } = Route.useSearch();
  const hayFicha = id !== undefined;

  const [verBajas, setVerBajas] = useState(false);
  const [editando, setEditando] = useState<Proveedor | null>(null);
  const [saldoEditando, setSaldoEditando] = useState<SaldoProveedor | null>(null);
  const [abierto, setAbierto] = useState(false);

  const { data, isFetching } = useProveedores(verBajas);
  // El nombre de la ficha, para el encabezado. Es la misma consulta que hace
  // la ficha adentro: sale de la cache, no es una vuelta más.
  const { data: ficha } = useFichaProveedor(id ?? null);
  // Para la fila del técnico. No hace falta endpoint nuevo: `deudaTecnico` es la
  // misma regla que usa el taller, leída desde acá para que la plata que se debe
  // se vea toda junta en un solo lugar.
  const { data: ordenesPagina } = useReparaciones({ limite: 200 });
  const crear = useCrearProveedor();
  const actualizar = useActualizarProveedor();

  const proveedores = useMemo(() => data?.filas ?? [], [data]);
  const tecnico = useMemo(() => deudaTecnico(ordenesPagina?.filas ?? []), [ordenesPagina]);

  function abrirAlta(): void {
    setEditando(null);
    setSaldoEditando(null);
    setAbierto(true);
  }

  /**
   * Editar desde la ficha, con el proveedor de la ficha. Antes se lo buscaba en
   * la lista de activos: uno dado de baja no estaba, `editando` quedaba en null
   * y se abría «Proveedor nuevo», con el riesgo de cargarlo dos veces.
   */
  function abrirEdicionDeLaFicha(p: SaldoProveedor, saldo: SaldoProveedor | null): void {
    setEditando(p);
    setSaldoEditando(saldo);
    setAbierto(true);
  }

  function guardar(cambios: CambiosProveedor): void {
    if (editando) {
      actualizar.mutate(
        { id: editando.id, cambios },
        {
          onSuccess: (p) => {
            setAbierto(false);
            mostrarExito(
              `${p.nombre} actualizado`,
              p.activo ? undefined : "Queda dado de baja: no aparece más para elegir.",
            );
          },
          onError: mostrarError,
        },
      );
      return;
    }

    // El alta usa el endpoint minimo, que es el mismo del wizard de compras. Lo
    // que el POST no acepta (direccion, codigo, observaciones) se manda en un
    // PATCH inmediato: es preferible a duplicar el esquema del alta en el
    // backend para un caso que casi siempre viene vacio.
    crear.mutate(
      { nombre: cambios.nombre ?? "", moneda_lista: cambios.moneda_lista },
      {
        onSuccess: (nuevo) => {
          const resto: CambiosProveedor = { ...cambios };
          delete resto.nombre;
          delete resto.moneda_lista;
          delete resto.activo;
          const hayResto = Object.values(resto).some((v) => v !== null && v !== undefined);

          if (!hayResto) {
            setAbierto(false);
            mostrarExito(`${nuevo.nombre} quedó cargado`);
            return;
          }

          actualizar.mutate(
            { id: nuevo.id, cambios: resto },
            {
              onSuccess: () => {
                setAbierto(false);
                mostrarExito(`${nuevo.nombre} quedó cargado`);
              },
              onError: mostrarError,
            },
          );
        },
        onError: mostrarError,
      },
    );
  }

  return (
    <Pagina>
      {hayFicha ? (
        <>
          <Button asChild type="button" variant="ghost" size="sm" className="self-start">
            <Link to="/proveedores" search={{}}>
              <ArrowLeft aria-hidden="true" />
              Volver a la lista
            </Link>
          </Button>
          {/* De quién es la cuenta. Sin esto el nombre solo aparecía adentro
              del diálogo de pago. */}
          {ficha ? (
            <PageHeader
              titulo={ficha.proveedor.nombre}
              icono={Truck}
              acciones={
                ficha.proveedor.activo ? null : <Badge variant="outline">Dado de baja</Badge>
              }
            />
          ) : null}
        </>
      ) : (
        <PageHeader
          titulo="Proveedores"
          icono={Truck}
          acciones={
            <Button type="button" onClick={abrirAlta}>
              <Plus aria-hidden="true" />
              Proveedor nuevo
            </Button>
          }
        />
      )}

      {hayFicha ? (
        <FichaProveedor proveedorId={id} onEditar={abrirEdicionDeLaFicha} />
      ) : (
        <>
          {/* Siempre a la vista. Dependía de contar las bajas en la lista, pero
              la lista trae solo los activos: nunca había bajas que contar y el
              conmutador no aparecía, así que un proveedor dado de baja (con su
              deuda) desaparecía sin forma de volver a verlo. */}
          <Checkbox
            label="Ver también los dados de baja"
            checked={verBajas}
            onChange={(e) => setVerBajas(e.target.checked)}
          />

          <ListaProveedores
            filas={proveedores}
            total={data?.total ?? 0}
            deudaTecnico={tecnico}
            cargando={isFetching}
          />
        </>
      )}

      <DialogProveedor
        abierto={abierto}
        proveedor={editando}
        saldo={saldoEditando}
        existentes={proveedores}
        guardando={crear.isPending || actualizar.isPending}
        onCerrar={() => setAbierto(false)}
        onGuardar={guardar}
      />
    </Pagina>
  );
}
